import { RootStore } from "./RootStore";
import { runBootstrapStep } from "./bootstrapStep";
import { makeAutoObservable, runInAction } from "mobx";
import posthog, { captureEvent } from "@/utils/posthog";
import * as compareVersions from "compare-versions";
import * as Sentry from "@sentry/react";
import { APP_TO_BUILD, FIREBASE_DB_CONFIG } from "../../appsConfig";
import { emailValidator } from "@/services/EmailValidator";
import { USER_ACCESS_LEVEL } from "@/utils/types";
import * as fns from "@/api/fetch";
import {
  getEmailPreferences,
  updateEmailPreferences,
  EmailPreferences,
} from "@/api/emailPreferences";
import {
  APP_PURCHASE_STATE,
  NewPurchaseState,
  getPurchaseAccessFromOldPurchaseState,
  mergePurchaseAccesses,
} from "@/utils/purchaseState";
import { getUser } from "@/lib/useAuthedFetch";
import { StorageKeys } from "@/utils/storage";
import { showToastFromKey } from "@/lib/toast";
import {
  isPlumbingLevelCode,
  mergeReferralTrialAccess,
  PLUMBING_DEFAULT_LEVEL_CODE,
} from "@petergok/blue-collar-shared";
import { isReferralOfferClaimable } from "@/referral/referralOfferEligibility";
import { hasUnlimitedAiMessages } from "@/utils/aiAccess";
import { hasFreeStudyAccess } from "@/utils/appEntitlements";
import { hasFullStoredAccessForOrgRedemption } from "@/utils/orgPurchases/redemptionPolicy";

/** Return current “version” on web (set VITE_APP_VERSION in .env) */
export const getDeviceVersion = (): string =>
  import.meta.env.VITE_APP_VERSION ?? "1.0.0";

const DEFAULT_EMAIL_PREFERENCES: EmailPreferences = {
  questionReports: true,
  examReminders: true,
  productUpdates: true,
};

export default class MainAppStore {
  rootStore: RootStore;

  // observables
  showForceUpgrade = false;
  storeInitialized = false;
  attemptedToInitializeIAP = false;
  appPurchaseState = APP_PURCHASE_STATE.TRIAL;
  processingPayment = false;
  showRestoreButton = false;
  /**
   * True when checkPurchaseState failed after its retry, so the entitlements currently
   * in the store are UNKNOWN rather than known-free. The UI must say so instead of
   * quietly rendering the free tier to someone who may well be a subscriber.
   */
  purchaseStateError = false;
  /**
   * True when the profile fetch failed during bootstrap. Kept separate from
   * `profileSaved` (which means "a profile was loaded"), because "we could not ask"
   * and "there is nothing to show" need different screens.
   */
  profileError = false;
  showAccessCode = false;
  inAppBannerInfo: any = null;
  products: any[] = [];

  appPriceLocalized?: string;
  upgradePriceLocalized?: string;
  subscriptionPriceLocalized?: string;
  lifetimePriceLocalized?: string;
  usSubscriptionPriceLocalized?: string;
  usAISubscriptionPriceLocalized?: string;

  profileName?: string;
  profileSchool?: string;
  profileReferralSource?: string;
  examDateSelection?: string;
  scheduledExamDate?: string;
  hasBookedExam?: boolean | null;
  profileEmail?: string;
  profileSaved = false;
  profilePersistedComplete = false;
  profileHasExplicitStateSelection = false;
  knowledgeLevel?: string;
  forcePurchaseState?: string;
  forcePaid?: boolean;
  codeRedeemed?: string;
  showCoachmark?: boolean;
  emailPreferences?: EmailPreferences;
  emailPreferencesLoading = false;
  emailPreferencesError = false;
  emailPreferencesLoadSequence = 0;
  emailPreferencesUpdateSequence = 0;

  selectedStateCode?: string;
  selectedCodebookYear: number | null = null;
  previousVersionCode?: string;

  recommendedTopicsForAI: any = {};
  questionGuides: Record<string, unknown> = {};

  newPurchaseState?: NewPurchaseState = {
    purchasedProducts: [],
    purchaseAccess: {},
  };
  // Tracks the user id whose newPurchaseState is currently loaded. Lets reset()
  // skip wiping purchase state when the same user re-logs in, so a transient
  // checkPurchaseState failure doesn't briefly downgrade the user to FREE.
  lastPurchaseStateUserId?: string;
  /**
   * Read-time merge of the stored purchaseAccess with an active referral
   * trial. NEVER persist or POST the result — gates read merged access,
   * state stores raw access.
   */
  private effectiveAccess = () =>
    mergeReferralTrialAccess(
      this.newPurchaseState?.purchaseAccess,
      this.newPurchaseState?.referralTrial,
      Date.now()
    );

  /**
   * Full access from stored provider/server access only. An active referral
   * trial inflates effectiveAccess(), but must not block a real access code.
   */
  private hasRealFullAccess = (): boolean =>
    hasFullStoredAccessForOrgRedemption(
      APP_TO_BUILD,
      this.newPurchaseState,
    );

  getOrgRedeemBlockedReason = (): "already_has_access" | null =>
    this.hasRealFullAccess() ? "already_has_access" : null;

  hasAiAccess = () => {
    return this.effectiveAccess().aiAccess?.active;
  };
  hasUnlimitedAiMessages = () => {
    return hasUnlimitedAiMessages(this.effectiveAccess());
  };
  hasStudyAccess = (inCurrentState: boolean) => {
    const access = this.effectiveAccess();
    const hasStudyAccess = access.studyAccess?.active;
    const hasAllLevelStudyAccess = access.allLevelStudyAccess?.active;

    if (APP_TO_BUILD === "electrical" && inCurrentState) {
      return (
        hasStudyAccess &&
        (hasAllLevelStudyAccess || this.selectedStateCode?.includes("RS_CON"))
      );
    }

    if (APP_TO_BUILD === "plumbing" && inCurrentState) {
      return (
        hasStudyAccess &&
        (hasAllLevelStudyAccess ||
          this.selectedStateCode === PLUMBING_DEFAULT_LEVEL_CODE)
      );
    }

    return hasFreeStudyAccess(APP_TO_BUILD) || hasStudyAccess;
  };
  hasAllLevelStudyAccess = () => {
    return this.effectiveAccess().allLevelStudyAccess?.active;
  };

  referralOffer = () => this.newPurchaseState?.referralOffer;

  referralTrial = () => this.newPurchaseState?.referralTrial;

  isPaidReferrer = () => this.newPurchaseState?.isPaidReferrer === true;

  isReferralOfferClaimable = () => {
    return isReferralOfferClaimable({
      offer: this.newPurchaseState?.referralOffer,
      referralTrial: this.newPurchaseState?.referralTrial,
      purchaseAccess: this.newPurchaseState?.purchaseAccess,
    });
  };

  isReferralOfferDismissed = () =>
    this.newPurchaseState?.referralOffer?.dismissedAt != null;

  /**
   * Set by the dismissed-offer reminder card to re-open the trial-offer modal
   * after the user dismissed it. The card and the modal live in different parts
   * of the tree, so this shared flag coordinates them.
   */
  referralOfferModalForceOpen = false;

  openReferralOfferModal = () => {
    this.referralOfferModalForceOpen = true;
  };

  closeReferralOfferModal = () => {
    this.referralOfferModalForceOpen = false;
  };

  shouldShowReferralExpiryPopup = () => {
    const trial = this.newPurchaseState?.referralTrial;
    return trial?.status === "expired" && !trial.expiryPopupSeen;
  };

  profileHasSeenTrialOffer = false;

  /** Mirrors PostHog 'nec-7-day-free-trial'; default OFF until flags load. */
  necTrialFlagEnabled = false;

  isNecTrialFlagEnabled = () => this.necTrialFlagEnabled;

  isStudyTrialEligible = () => {
    return (
      APP_TO_BUILD === "electrical_us" &&
      this.newPurchaseState?.studyTrialEligible === true &&
      this.isNecTrialFlagEnabled()
    );
  };

  isStudyTrialExpired = () => {
    return (
      APP_TO_BUILD === "electrical_us" &&
      this.newPurchaseState?.studyTrial?.status === "expired"
    );
  };

  // UI affordance only — the real gate is `isAppAdminEmail` on the server, which
  // evaluates the Clerk-verified primary email. This check reads `profileEmail`
  // from client store state and is therefore trivially spoofable; it must never
  // be relied on for authorisation.
  //
  // `@devmenutest.com` removed to stay in step with the server list: that domain
  // was not registered to anyone, so it was purchasable by an attacker who could
  // then verify an address on it legitimately.
  isAdmin = () => {
    const email = this.profileEmail;
    return (
      !!email &&
      (email.endsWith("@tempo.new") ||
        email.endsWith("@dakotaprep.com") ||
        email.includes("christinesabe27"))
    );
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.initStore();

    makeAutoObservable(this, {
      rootStore: false,
      emailPreferencesLoadSequence: false,
      emailPreferencesUpdateSequence: false,
    });

    // Keep the NEC trial flag observable in sync with PostHog. onFeatureFlags
    // fires on initial load and on every reload (incl. after identify), so a
    // user who becomes eligible mid-session is re-evaluated automatically.
    posthog.onFeatureFlags(() => {
      runInAction(() => {
        this.necTrialFlagEnabled =
          posthog.getFeatureFlag("nec-7-day-free-trial") === true;
      });
    });
  }

  clearEmailPreferencesCache = () => {
    this.emailPreferences = undefined;
    this.emailPreferencesLoading = false;
    this.emailPreferencesError = false;
    this.emailPreferencesLoadSequence += 1;
    this.emailPreferencesUpdateSequence += 1;
    localStorage.removeItem(StorageKeys.EMAIL_PREFERENCES);
  };

  reset = async () => {
    console.log("[SPLASH DEBUG] MainAppStore reset");

    this.showForceUpgrade = false;
    this.attemptedToInitializeIAP = false;
    // Only clear purchase state if a different user (or unknown) is signing in.
    // Same-user re-logins keep the prior state during the refresh window so a
    // transient checkPurchaseState error can't drop a paid/trialing user to FREE.
    let currentUserId: string | undefined;
    try {
      const u = await getUser();
      currentUserId = u?.id;
    } catch {
      currentUserId = undefined;
    }
    if (!currentUserId || currentUserId !== this.lastPurchaseStateUserId) {
      this.newPurchaseState = {
        purchasedProducts: [],
        purchaseAccess: {},
      };
    }
    this.lastPurchaseStateUserId = currentUserId;
    this.processingPayment = false;
    this.showRestoreButton = false;
    this.purchaseStateError = false;
    this.profileError = false;
    this.showAccessCode = false;
    this.inAppBannerInfo = null;

    this.appPriceLocalized = undefined;
    this.upgradePriceLocalized = undefined;
    this.subscriptionPriceLocalized = undefined;
    this.lifetimePriceLocalized = undefined;

    this.usSubscriptionPriceLocalized = undefined;
    this.usAISubscriptionPriceLocalized = undefined;

    this.profileName = undefined;
    this.profileSchool = undefined;
    this.profileReferralSource = undefined;
    this.examDateSelection = undefined;
    this.scheduledExamDate = undefined;
    this.hasBookedExam = undefined;
    this.profileEmail = undefined;
    this.profileSaved = undefined;
    this.profilePersistedComplete = false;
    this.profileHasExplicitStateSelection = false;
    this.knowledgeLevel = undefined;
    this.forcePurchaseState = undefined;
    this.forcePaid = undefined;
    this.showCoachmark = undefined;
    this.clearEmailPreferencesCache();

    this.selectedStateCode = undefined;
    this.selectedCodebookYear = null;
    this.profileHasSeenTrialOffer = false;

    this.previousVersionCode = undefined;
    this.questionGuides = {};

    await this.initStore();

    return;
  };

  async initStore() {
    console.log("[SPLASH DEBUG] MainAppStore (web) init started");
    this.loadShowRestoreButton();
    this.loadHideAccessCode();
    this.loadInAppBannerInfo();

    this.loadPreviousVersionCode()
      .then(() => this.registerDevice())
      .catch((error) => {
        Sentry.captureException(error);
        this.registerDevice();
      });

    // `storeInitialized` gates the splash screen (App.tsx). Each step is contained
    // INDEPENDENTLY so that one failure cannot cancel the steps after it.
    //
    // A single try around a sequential await chain was not enough. Only two steps here
    // can actually throw — `loadProfileData` (no internal catch) and `posthogIdentify`
    // — and a thrown `loadProfileData` skipped `checkPurchaseState` entirely. That is
    // the one combination the recovery banner cannot detect: the user boots with
    // entitlement never fetched AND `purchaseStateError` still false, so a subscriber
    // is shown the free tier with no error and no retry. Entitlement is the step that
    // must never be skipped.
    const [profileLoaded] = await Promise.all([
      this.bootstrapStep("profile", () => this.loadProfileData()),
      this.bootstrapStep("email_preferences", () => this.loadEmailPreferences()),
      // Optional, and nothing below depends on it — it only feeds AI-chat suggestions.
      this.bootstrapStep("recommended_topics", () =>
        this.getRecommendedTopicsForAIChat()
      ),
      this.bootstrapStep("question_guides", () => this.getQuestionGuides()),
    ]);

    runInAction(() => {
      this.profileError = !profileLoaded;
    });

    await this.bootstrapStep("products", () => this.loadProducts());

    // Entitlement. Runs unconditionally, including after a profile failure: whether a
    // user has paid does not depend on their profile document loading.
    await this.bootstrapStep("purchase_state", () => this.checkPurchaseState());

    await this.bootstrapStep("analytics_identify", () => this.posthogIdentify());

    runInAction(() => {
      this.storeInitialized = true;
    });
    return;
  }

  /** Runs one bootstrap step in isolation — see state/bootstrapStep. */
  private bootstrapStep(step: string, run: () => Promise<unknown>) {
    return runBootstrapStep(step, run, ({ error }) => {
      console.error(`initStore step "${step}" failed; continuing:`, error);
      Sentry.captureException(error, { tags: { area: "store_init", step } });
    });
  }

  async loadProducts() {
    // Products only feed the paywall, but this runs on the awaited boot path in
    // `initStore`. Without this catch a Stripe outage throws, `storeInitialized`
    // is never set, and every user — including those who never open pricing —
    // is stranded on the splash screen indefinitely.
    try {
      const response = (await fns.getProducts()) as { products: any[] };
      if (response.products) this.products = response.products;
    } catch (e) {
      Sentry.captureException(e);
    }
    return;
  }

  /** Redeem an access code in Firestore */
  /**
   * Returns a result code: "Success" | "NOT_FOUND" | "ALREADY_REDEEMED" | "ERROR".
   * The UI maps these to localized strings via the `errors.redeem_*` keys.
   */
  async redeemCode(code: string): Promise<string> {
    try {
      const data = (await fns.getAccessCode(code)) as {
        redeemed?: boolean;
        purchase_type?: APP_PURCHASE_STATE;
      } | null;

      if (!data) {
        return "NOT_FOUND";
      }

      if (data.redeemed) {
        return "ALREADY_REDEEMED";
      }

      await fns.redeemAccessCode(code);

      const newPurchaseState = getPurchaseAccessFromOldPurchaseState(
        data.purchase_type || APP_PURCHASE_STATE.OLD_PURCHASE,
        "Redeem Code"
      );
      const mergedAccess = mergePurchaseAccesses([
        this.newPurchaseState.purchaseAccess,
        newPurchaseState,
      ]);

      this.newPurchaseState = {
        ...this.newPurchaseState,
        purchaseAccess: mergedAccess,
      };

      this.codeRedeemed = code;

      await this.checkPurchaseState();

      captureEvent("redeemed_code");
      return "Success";
    } catch (e) {
      if (e.message?.includes("Code not found")) return "NOT_FOUND";

      return "ERROR";
    }
  }

  /** Register this device in Firestore */
  async registerDevice() {
    try {
      await fns.upsertProfile({
        opened_app: true,
        latest_version: getDeviceVersion(),
        last_updated: new Date().toISOString(),
      });
    } catch (error) {
      console.error("registerDevice error:", error);
      Sentry.captureException(error);
    }
  }

  /** Fetch & apply purchase state */
  async checkPurchaseState() {
    const attempt = async () => {
      const res = (await fns.checkPurchaseState()) as any;
      if (!res?.purchaseState) {
        throw new Error(
          "checkPurchaseState: missing purchaseState in response"
        );
      }
      this.newPurchaseState = res.purchaseState;
      try {
        const u = await getUser();
        this.lastPurchaseStateUserId = u?.id;
      } catch (userError) {
        Sentry.captureException(userError, {
          tags: { area: "purchase_state", phase: "user_id_tracking" },
        });
      }
      await this.updatePostHogProperties();
    };

    try {
      await attempt();
      runInAction(() => {
        this.purchaseStateError = false;
      });
    } catch (e) {
      Sentry.captureException(e, {
        tags: { area: "purchase_state", phase: "initial" },
      });
      // One retry to absorb post-login auth races and backend cold starts —
      // without this a single transient failure leaves the user looking FREE
      // even when they have an active trial / paid subscription.
      try {
        await new Promise((r) => setTimeout(r, 600));
        await attempt();
        runInAction(() => {
          this.purchaseStateError = false;
        });
      } catch (e2) {
        Sentry.captureException(e2, {
          tags: { area: "purchase_state", phase: "retry" },
        });
        console.error("checkPurchaseState failed after retry:", e2);
        // Record the failure so the UI can say "we could not confirm your subscription"
        // instead of silently rendering the free tier. On a fresh page load
        // `lastPurchaseStateUserId` is undefined (it is never persisted), so reset()
        // has already cleared purchase state — meaning a subscriber whose check fails
        // twice is shown locked content and upgrade prompts for something they own,
        // with no way to recover. `showRestoreButton` exists in this store but NO
        // component reads it, so there is no restore-purchases UI on web at all.
        runInAction(() => {
          this.purchaseStateError = true;
        });
      }
    }
    return;
  }

  /** Retries the purchase-state check after a failure. Used by the recovery banner. */
  retryPurchaseState = async () => {
    await this.checkPurchaseState();
  };

  /** Retries the profile fetch after a bootstrap failure. Used by the recovery banner. */
  retryProfileLoad = async () => {
    const loaded = await runBootstrapStep(
      "profile_retry",
      () => this.loadProfileData(),
      ({ error }) => {
        console.error("profile retry failed:", error);
        Sentry.captureException(error, {
          tags: { area: "store_init", step: "profile_retry" },
        });
      }
    );
    runInAction(() => {
      this.profileError = !loaded;
    });
  };

  /** Firestore flag for restore button */
  loadShowRestoreButton() {
    fns
      .getShowRestore()
      .then((snap: any) => {
        const min = snap.data()?.min_version;
        if (min && compareVersions.compare(getDeviceVersion(), min, ">=")) {
          this.showRestoreButton = true;
        }
      })
      .catch((e) => Sentry.captureException(e));
  }

  /** Firestore flag to hide access code */
  loadHideAccessCode() {
    fns
      .getHideAccessCodes()
      .then((snap: any) => {
        const min = snap.data()?.min_version;
        this.showAccessCode =
          !min || compareVersions.compare(getDeviceVersion(), min, "<");
      })
      .catch((e) => Sentry.captureException(e));
  }

  /** Firestore banner info */
  loadInAppBannerInfo() {
    fns
      .getInAppBannerInfo()
      .then((snap: any) => {
        this.inAppBannerInfo = snap.data();
      })
      .catch((e) => Sentry.captureException(e));
  }

  /** Load previous version from Firestore */
  async loadPreviousVersionCode() {
    const snap = (await fns.getDevice()) as any;
    this.previousVersionCode = snap?.latest_version;
  }

  /** Persist a new state code */
  async setSelectedStateCode(
    stateCode: string,
    inSignUp?: boolean,
    { clearCodebookYear = true }: { clearCodebookYear?: boolean } = {}
  ) {
    this.selectedStateCode = stateCode;

    if (clearCodebookYear) {
      this.selectedCodebookYear = null;
    }

    if (!inSignUp) {
      await fns.upsertProfile({
        current_state_code: stateCode,
        ...(clearCodebookYear && { codebook_year: null }),
      });
    }
  }

  private applyDefaultPlumbingLevel = async () => {
    if (APP_TO_BUILD !== "plumbing" || this.selectedStateCode) return;

    this.selectedStateCode = PLUMBING_DEFAULT_LEVEL_CODE;
    try {
      await fns.upsertProfile({
        current_state_code: PLUMBING_DEFAULT_LEVEL_CODE,
      });
    } catch (e) {
      console.error("Error defaulting plumbing level in profile:", e);
      Sentry.captureException(e);
    }
  };

  /** Persist a codebook year override (or clear it with null) */
  async setSelectedCodebookYear(year: number | null) {
    this.selectedCodebookYear = year;
    try {
      await fns.upsertProfile({ codebook_year: year });
    } catch (e) {
      console.error("Error updating codebook_year in Firestore:", e);
      Sentry.captureException(e);
    }
  }

  /** Compute user’s access level */
  getUserAccessLevel(): USER_ACCESS_LEVEL {
    if (
      this.appPurchaseState === APP_PURCHASE_STATE.TRIAL ||
      this.appPurchaseState === APP_PURCHASE_STATE.CONFIRMED_TRIAL
    ) {
      return USER_ACCESS_LEVEL.FREE;
    }
    if (this.appPurchaseState === APP_PURCHASE_STATE.OLD_PURCHASE) {
      return USER_ACCESS_LEVEL.PRO;
    }
    if (
      this.appPurchaseState === APP_PURCHASE_STATE.US_SUBSCRIBED_WITH_AI ||
      this.appPurchaseState === APP_PURCHASE_STATE.US_SUBSCRIBED
    ) {
      return USER_ACCESS_LEVEL.PRO_AI;
    }
    return APP_TO_BUILD === "electrical"
      ? USER_ACCESS_LEVEL.ALL_ACCESS
      : USER_ACCESS_LEVEL.PRO;
  }

  /** Load profile from storage or Firestore */
  async loadProfileData() {
    const d = (await fns.getProfile()) as any;
    this.profileEmail = d.profile_email;
    this.profileName = d.profile_name;
    this.profileSchool = d.profile_school;
    this.profileReferralSource = d.referral_source;
    this.examDateSelection = d.exam_date_selection;
    this.scheduledExamDate = d.scheduled_exam_date;
    this.hasBookedExam = d.has_booked_exam ?? null;
    this.knowledgeLevel = d.knowledge_level;
    // Use direct assignment to avoid triggering upsertProfile on load,
    // except when legacy plumbing users need the previous Red Seal default.
    if (APP_TO_BUILD === "plumbing") {
      if (isPlumbingLevelCode(d.current_state_code)) {
        this.profileHasExplicitStateSelection = true;
        this.selectedStateCode = d.current_state_code;
      } else {
        this.profileHasExplicitStateSelection = false;
        this.selectedStateCode = undefined;
        await this.applyDefaultPlumbingLevel();
      }
    } else {
      this.profileHasExplicitStateSelection = Boolean(d.current_state_code);
      this.selectedStateCode = d.current_state_code;
    }
    if (d.codebook_year != null) {
      this.selectedCodebookYear = d.codebook_year;
    }
    this.showCoachmark = !d.have_shown_coachmark;
    this.profileHasSeenTrialOffer = d.has_seen_trial_offer === true;
    this.profilePersistedComplete = this.isProfileComplete;
    this.profileSaved = true;

    return;
  }

  async loadEmailPreferences() {
    const loadSequence = this.emailPreferencesLoadSequence + 1;
    this.emailPreferencesLoadSequence = loadSequence;
    const updateSequenceAtStart = this.emailPreferencesUpdateSequence;
    const shouldApplyRefresh = () =>
      loadSequence === this.emailPreferencesLoadSequence &&
      updateSequenceAtStart === this.emailPreferencesUpdateSequence;

    this.emailPreferencesLoading = !this.emailPreferences;
    this.emailPreferencesError = false;

    const cached = localStorage.getItem(StorageKeys.EMAIL_PREFERENCES);
    if (cached) {
      try {
        if (shouldApplyRefresh()) {
          this.emailPreferences = JSON.parse(cached) as EmailPreferences;
          this.emailPreferencesLoading = false;
        }
      } catch {
        localStorage.removeItem(StorageKeys.EMAIL_PREFERENCES);
      }
    }

    try {
      const prefs = await getEmailPreferences();
      if (shouldApplyRefresh()) {
        this.emailPreferences = prefs;
        localStorage.setItem(
          StorageKeys.EMAIL_PREFERENCES,
          JSON.stringify(prefs)
        );
      }
    } catch (e) {
      if (shouldApplyRefresh()) {
        console.error(e);
        Sentry.captureException(e);
        this.emailPreferencesError = true;
      }
    } finally {
      if (loadSequence === this.emailPreferencesLoadSequence) {
        this.emailPreferencesLoading = false;
      }
    }
  }

  // Immediate optimistic update — no network call. Pair with persistEmailPreference
  // when the caller wants to debounce rapid toggles without delaying the UI.
  //
  // Bumping the update sequence here is what makes the debounce safe: any
  // in-flight PUT started by a previous persistEmailPreference call will see
  // a stale sequence when it settles and discard its response, so a slow
  // network round-trip can't overwrite a newer local toggle.
  setEmailPreferenceLocal(key: keyof EmailPreferences, value: boolean) {
    this.emailPreferencesUpdateSequence += 1;
    const previous = this.emailPreferences || DEFAULT_EMAIL_PREFERENCES;
    const next = { ...previous, [key]: value };
    this.emailPreferences = next;
    this.emailPreferencesError = false;
    localStorage.setItem(
      StorageKeys.EMAIL_PREFERENCES,
      JSON.stringify(next)
    );
  }

  // Network-only persistence with sequence tracking. On error, refetches the
  // server truth instead of rolling back to a stale "previous" — after rapid
  // debounced toggles, "previous" no longer reflects an authoritative state.
  async persistEmailPreference(
    key: keyof EmailPreferences,
    value: boolean,
  ) {
    const updateSequence = this.emailPreferencesUpdateSequence + 1;
    this.emailPreferencesUpdateSequence = updateSequence;

    try {
      const updated = await updateEmailPreferences({ [key]: value });
      if (updateSequence === this.emailPreferencesUpdateSequence) {
        this.emailPreferences = updated;
        this.emailPreferencesError = false;
        localStorage.setItem(
          StorageKeys.EMAIL_PREFERENCES,
          JSON.stringify(updated)
        );
      }
      return updated;
    } catch (e) {
      if (updateSequence === this.emailPreferencesUpdateSequence) {
        this.emailPreferencesError = true;
        // Realign with server after a failed write — rapid debounced toggles
        // make a local rollback unreliable.
        void this.loadEmailPreferences();
      }
      throw e;
    }
  }

  async updateEmailPreference(key: keyof EmailPreferences, value: boolean) {
    this.setEmailPreferenceLocal(key, value);
    return this.persistEmailPreference(key, value);
  }

  setProfileName = (name: string) => {
    this.profileName = name;
  };

  setProfileSchool = (school: string) => {
    this.profileSchool = school;
  };

  setProfileEmail = (email: string) => {
    this.profileEmail = email ? email.toLowerCase() : email;
  };

  setProfileReferralSource = (referralSource: string) => {
    this.profileReferralSource = referralSource;
  };

  setExamDateSelection = (examDateSelection: string) => {
    this.examDateSelection = examDateSelection || undefined;
    if (examDateSelection) this.scheduledExamDate = undefined;
  };

  setScheduledExamDate = (scheduledExamDate: string) => {
    this.scheduledExamDate = scheduledExamDate || undefined;
    if (scheduledExamDate) this.examDateSelection = undefined;
  };

  setHasBookedExam = (value: boolean | null) => {
    this.hasBookedExam = value;
  };

  get isProfileComplete(): boolean {
    // hasBookedExam == null covers both null (unanswered) and undefined (old user data pre-feature)
    const examBookingComplete =
      (this.hasBookedExam === true && !!this.scheduledExamDate) ||
      (this.hasBookedExam === false && !!this.examDateSelection) ||
      (this.hasBookedExam == null &&
        (!!this.scheduledExamDate || !!this.examDateSelection));

    return !!(
      this.profileName &&
      this.profileSchool &&
      this.knowledgeLevel &&
      examBookingComplete &&
      this.profileReferralSource
    );
  }

  setKnowledgeLevel = (knowledgeLevel: string) => {
    this.knowledgeLevel = knowledgeLevel;
  };

  /** Save profile to Firestore */
  async saveProfile(config?: { validateEmail?: boolean }): Promise<boolean> {
    const user = await getUser();
    const emailLC = this.profileEmail?.toLowerCase();
    if (emailLC && config?.validateEmail) {
      const { valid } = await emailValidator.validate(emailLC);
      if (!valid) {
        showToastFromKey({
          titleKey: "errors.invalid_email_title",
          descriptionKey: "errors.invalid_email_body",
          variant: "destructive",
        });
        return false;
      }
    }

    try {
      await fns.upsertProfile({
        profile_email: emailLC,
        profile_name: this.profileName,
        profile_school: this.profileSchool,
        referral_source: this.profileReferralSource,
        exam_date_selection: this.examDateSelection ?? null,
        scheduled_exam_date: this.scheduledExamDate ?? null,
        has_booked_exam: this.hasBookedExam ?? null,
        knowledge_level: this.knowledgeLevel,
        current_state_code: this.selectedStateCode,
      });
    } catch (error) {
      console.error("saveProfile firestore error:", error);
      Sentry.captureException(error);
      // Tell the user. Returning a bare `false` re-enabled the Continue button with no
      // explanation, so the screen looked like the click did nothing — and a paid user
      // with an incomplete profile is force-redirected back to /create-profile from
      // everywhere else, so a silent failure here is a closed loop they cannot escape.
      // Distinct from the invalid-email path above, which toasts its own message.
      showToastFromKey({
        titleKey: "errors.generic_title",
        descriptionKey: "errors.profile_save_failed_body",
        variant: "destructive",
      });
      return false;
    }

    this.profilePersistedComplete = this.isProfileComplete;
    this.profileSaved = true;
    await this.posthogIdentify();
    return true;
  }

  /** Build PostHog user properties object */
  private async buildPostHogUserProperties() {
    const user = await getUser();
    // Determine which access types the user has
    const hasAiAccess = this.hasAiAccess();
    const hasAllLevelStudyAccess = this.hasAllLevelStudyAccess();
    const hasStudyAccess = this.hasStudyAccess(false);
    // Attribution sources must come from the same merged access the has* flags
    // read, so a referral-trial grant reports source 'referral_trial' instead
    // of a null attribution. Read-only — never persisted.
    const effectiveAccess = this.effectiveAccess();

    // Build array of active access types
    const accessTypes: string[] = [];
    if (hasAiAccess) accessTypes.push("aiAccess");
    if (hasAllLevelStudyAccess) accessTypes.push("allLevelStudyAccess");
    if (hasStudyAccess) accessTypes.push("studyAccess");

    return {
      email: this.profileEmail ?? user?.emailAddresses[0].emailAddress,
      name: this.profileName ?? null,
      school: this.profileSchool ?? null,
      referralSource: this.profileReferralSource ?? null,
      examDateSelection: this.examDateSelection ?? null,
      selectedStateCode: this.selectedStateCode ?? null,
      new_purchase_state: this.newPurchaseState.purchaseAccess,
      force_purchase_state: this.forcePurchaseState ?? null,
      force_paid: this.forcePaid ?? null,
      // `access_code` deliberately omitted. An organisation access code is a
      // bearer credential — whoever holds it can redeem a seat — and sending it
      // here made it a DURABLE person property (`$set`), which outlives the
      // event that carried it and cannot be removed by deleting that event.
      // Whether a user redeemed a code is captured by the entitlement flags
      // below; the code itself has no analytics value.
      authentication_id: user?.emailAddresses[0].emailAddress,
      is_paid_user: accessTypes.length > 0,
      has_ai_access: hasAiAccess,
      has_all_level_study_access: hasAllLevelStudyAccess,
      has_study_access: hasStudyAccess,
      paid_access_given: accessTypes,
      ai_access_attribution: hasAiAccess
        ? effectiveAccess.aiAccess?.source || null
        : null,
      study_access_attribution: hasStudyAccess
        ? effectiveAccess.studyAccess?.source || null
        : null,
      all_level_study_access_attribution: hasAllLevelStudyAccess
        ? effectiveAccess.allLevelStudyAccess?.source || null
        : null,
    };
  }

  /** Update PostHog person properties (hydrate after state changes) */
  async updatePostHogProperties() {
    const props = await this.buildPostHogUserProperties();

    // Use $set to update person properties without creating a new identify event
    posthog.capture("$set", { $set: props });
  }

  /** Identify user in PostHog (call on session start) */
  async posthogIdentify() {
    const user = await getUser();
    const props = await this.buildPostHogUserProperties();

    posthog.identify(user?.id, props);

    captureEvent("session_start", { $set: props });
    fns.upsertProfile({
      authentication_id: user?.emailAddresses[0].emailAddress,
      profile_email: this.profileEmail || user?.emailAddresses[0].emailAddress,
    });
    return;
  }

  /** Reset profile (Firestore + storage) */
  async resetProfile() {
    try {
      await fns.upsertProfile({
        profile_email: null,
        profile_school: null,
        profile_name: null,
        exam_date_selection: null,
        scheduled_exam_date: null,
        has_booked_exam: null,
        knowledge_level: null,
      });
    } catch (error) {
      console.error("resetProfile firestore error:", error);
      Sentry.captureException(error);
    }

    this.profileEmail = undefined;
    this.profileName = undefined;
    this.profileSchool = undefined;
    this.profileReferralSource = undefined;
    this.examDateSelection = undefined;
    this.scheduledExamDate = undefined;
    this.hasBookedExam = undefined;
    this.knowledgeLevel = undefined;
    this.profileSaved = false;
    this.profilePersistedComplete = false;
    this.profileHasExplicitStateSelection = false;
    posthog.reset?.();
  }

  /** Generate random ID */
  private makeId(length: number): string {
    let result = "";
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /** Hide the upgrade prompt */
  clearPreviousVersionCode() {
    this.previousVersionCode = undefined;
  }

  /** Remove saved state code */
  resetSelectedStateCode() {
    this.selectedStateCode = undefined;
    this.selectedCodebookYear = null;
  }

  setNewForcePurchaseState = async (purchaseAccess: {
    aiAccess?: boolean;
    studyAccess?: boolean;
    allLevelStudyAccess?: boolean;
  }) => {
    this.newPurchaseState.forcePurchaseAccess = purchaseAccess;

    try {
      await fns.upsertPurchaseState({
        forcePurchaseAccess: purchaseAccess,
      });
    } catch (e) {
      console.error("Error updating new forcePurchaseAccess in Firestore:", e);
    }
  };

  /** Force-set purchase state for support */
  async setForcePurchaseState(state: string) {
    this.forcePurchaseState = state;
    try {
      await fns.upsertProfile({ force_purchase_state: state });
    } catch (error) {
      console.error("setForcePurchaseState error:", error);
      Sentry.captureException(error);
    }
  }

  async getRecommendedTopicsForAIChat() {
    try {
      const response = (await fns.getCollectionAsObject({
        collectionName: FIREBASE_DB_CONFIG.lessons,
        orderKey: "ranking",
        direction: "asc",
      })) as any;
      this.recommendedTopicsForAI = response.result;
    } catch (error) {
      console.error("getRecommendedTopicsForAIChat error:", error);
      Sentry.captureException(error);
      return [];
    }
  }

  async getQuestionGuides() {
    if (!FIREBASE_DB_CONFIG.questionGuides) {
      this.questionGuides = {};
      return;
    }

    try {
      const response = (await fns.getCollectionAsObject({
        collectionName: FIREBASE_DB_CONFIG.questionGuides,
        orderKey: "slug",
        direction: "asc",
      })) as { result?: Record<string, unknown> };
      this.questionGuides = response.result || {};
    } catch (error) {
      this.questionGuides = {};
      console.error("getQuestionGuides error:", error);
      Sentry.captureException(error);
    }
  }
}
